import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { useWhatsNewState } from "../hooks/useWhatsNewState";
import { useUserLeagues } from "../hooks/useUserLeagues";

// A small arrow that nudges toward the label, to draw the eye to the card
// without making it any bigger. Inline, so it takes no extra row. Held still
// for anyone who has asked the system to reduce motion.
function NudgeArrow() {
  return (
    <>
      <style>{`
        @keyframes whatsnew-nudge {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(3px); }
        }
        .whatsnew-nudge { animation: whatsnew-nudge 1.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .whatsnew-nudge { animation: none; }
        }
      `}</style>
      <ArrowRight aria-hidden="true" size={12} strokeWidth={2.5} color="var(--color-brand)" className="whatsnew-nudge" style={{ flexShrink: 0 }} />
    </>
  );
}

// Collapsed down to one line, no title: the bullets below carry the detail,
// this is just enough to say there's something to reopen.
function CollapsedRow({ onExpand }) {
  return (
    <button
      type="button"
      onClick={onExpand}
      className="flex items-center gap-1.5"
      style={{ marginBottom: 20, color: "var(--color-text-muted)", background: "none", border: "none", padding: 0, cursor: "pointer" }}
    >
      <NudgeArrow />
      <span style={{ fontSize: 12 }}>What's new in this version</span>
      <ChevronDown size={12} />
    </button>
  );
}

function WhatsNewCard() {
  const { latest, state, collapse, expand } = useWhatsNewState();
  // A bullet's link needs a leagueId to point into, and this card renders
  // on Home before a manager has necessarily picked one for the session.
  // Only offer the link when there's exactly one league to send them to;
  // with zero or several, the bullet stays plain text rather than guessing
  // which league it means.
  const { leagues } = useUserLeagues();
  const singleLeagueId = leagues?.length === 1 ? leagues[0].id : null;

  if (!latest) return null;

  if (state === "collapsed") {
    return <CollapsedRow onExpand={expand} />;
  }

  return (
    <div
      className="rounded-2xl shadow-sm"
      style={{
        backgroundColor: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        padding: "12px 14px",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <NudgeArrow />
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-muted)" }}>What's New In This Version</span>
        </span>
        <button type="button" onClick={collapse} aria-label="Collapse What's new" style={{ color: "var(--color-text-faint)", background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex" }}>
          <ChevronUp size={14} />
        </button>
      </div>
      <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5 }}>
        {latest.items.map((item) => (
          <li key={item.text} style={{ display: "flex", gap: 6, fontSize: 12, color: "var(--color-text-muted)" }}>
            <span aria-hidden="true">🏈</span>
            <div>
            <span>
              {item.isNew && (
                <span style={{ color: "var(--color-brand)", fontWeight: 700, marginRight: 4 }}>[NEW]</span>
              )}
              {item.text}
              {item.linkTo && singleLeagueId && (
                <>
                  {" "}
                  <Link to={`/${singleLeagueId}/${item.linkTo}`} style={{ color: "var(--color-brand)", fontWeight: 600 }}>
                    {item.linkLabel}
                  </Link>
                </>
              )}
            </span>
            {item.subItems?.length > 0 && (
              <ul style={{ margin: "4px 0 0", paddingLeft: 14, listStyle: "disc", display: "flex", flexDirection: "column", gap: 3 }}>
                {item.subItems.map((sub) => (
                  <li key={sub} style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{sub}</li>
                ))}
              </ul>
            )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default WhatsNewCard;
